import { Button, Group } from '@mantine/core';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import MentionableEditor, {
    MentionableEditorRef,
} from '@/features/comment/components/MentionableEditor';
import { convertJsonToPlainTextWithMentions } from '@/features/comment/utils/mentionUtils';
import { useCreateDailyScrumComment } from '../services/useDailyScrumCommentQueries';

interface DailyScrumCommentFormProps {
    parentId?: string;
    itemId: string;
    onCommentAdded?: () => void;
}

export const DailyScrumCommentForm: React.FC<DailyScrumCommentFormProps> = ({
    parentId,
    itemId,
    onCommentAdded,
}) => {
    const { t } = useTranslation('comment');
    const { user } = useAuthStore();
    const [comment, setComment] = useState('');
    const addCommentMutation = useCreateDailyScrumComment();

    // Prevent unnecessary re-renders
    const mentionableUsers = useMemo(() => [], []);

    const editorRef = useRef<MentionableEditorRef>(null);

    useEffect(() => {
        if (addCommentMutation.isSuccess && onCommentAdded) {
            onCommentAdded();
        }
    }, [addCommentMutation.isSuccess, onCommentAdded]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (comment.trim() && itemId && user?.id) {
            const commentData = {
                content: comment,
                parent_comment_id: parentId,
                daily_scrum_item_id: itemId,
            };
            
            addCommentMutation.mutate(commentData);

            // Reset state
            setComment('');
            editorRef.current?.clearContent();
        }
    };

    return (
        <Group
            component="form"
            gap="xs"
            ml={parentId ? 22 : 0}
            mt={parentId ? 12 : 0}
            onSubmit={handleSubmit}
        >
            <MentionableEditor
                ref={editorRef}
                mentionable={false}
                mentionableUsers={mentionableUsers}
                loadingUsers={false}
                onChange={(content, mentions, json) => {
                    const plainTextWithMentions =
                        convertJsonToPlainTextWithMentions(json);
                    setComment(plainTextWithMentions);
                }}
                autoFocus={parentId ? true : false}
            />
            <Button
                type="submit"
                variant="filled"
                size="sm"
                radius="md"
                disabled={
                    !comment.trim() || addCommentMutation.isPending || !user
                }
            >
                {t('common:action.enter')}
            </Button>
        </Group>
    );
}; 